import { isEmbedded } from '@/config';
import { PATH, USER_GUIDE_LINK } from '@/constants/path';
import cacheSlice, { isCacheSelector } from '@/redux/slice/cache.slice';
import { isLoadingGeneralSettingsSelector } from '@/redux/slice/loading';
import { Button, ButtonGroup, Frame, Icon, Page, PageProps } from '@shopify/polaris';
import { ChartLineIcon, ExternalIcon, LanguageIcon, PlanIcon, SettingsIcon, ShieldCheckMarkIcon } from '@shopify/polaris-icons';
import { memo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../Common/Toast';
import RegularText from '../RegularText';
import SkeletonPage from '../SkeletonPage';
import { LayoutStyled } from './styled';

const btnGroup = [
  {
    content: 'Onboarding',
    icon: ShieldCheckMarkIcon,
    path: PATH.WELCOME,
  },
  {
    content: 'Home',
    icon: ShieldCheckMarkIcon,
    path: PATH.DEFAULT,
  },
  {
    content: 'Estimate rules',
    icon: SettingsIcon,
    path: PATH.RULE_SETUP,
  },
  {
    content: 'Appearance',
    icon: SettingsIcon,
    path: PATH.APPEARANCE,
  },
  // {
  //   content: 'ETA checkout extension',
  //   icon: CartIcon,
  //   path: PATH.CHECKOUT_EXTENSION,
  // },
  {
    content: 'Translation',
    icon: LanguageIcon,
    path: PATH.TRANSLATION,
  },
  {
    content: 'General settings',
    icon: SettingsIcon,
    path: PATH.GENERAL_SETTINGS,
  },
  {
    content: 'Analytics',
    icon: ChartLineIcon,
    path: PATH.ANALYTICS,
  },
  // {
  //   content: 'Feedback',
  //   icon: ChatIcon,
  //   path: PATH.FEEDBACK,
  // },
  {
    content: 'Pricing plan',
    icon: PlanIcon,
    path: PATH.PRICING_PLAN,
  },
];

interface ILayoutProps extends PageProps {
  headers?: React.ReactNode;
  noPaddingInButton?: boolean;
}

const Layout = ({ ...props }: ILayoutProps): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoading = useSelector(isLoadingGeneralSettingsSelector);
  const isCache = useSelector(isCacheSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isCache && !isLoading) {
      const timer = setTimeout(() => {
        dispatch(cacheSlice.actions.handleCache(true));
        return () => clearTimeout(timer);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <LayoutStyled noPaddingInButton={props.noPaddingInButton}>
      <Frame>
        <Toast />
        {!isEmbedded ? (
          <div className="nav-bar">
            <ButtonGroup fullWidth gap="extraTight">
              {btnGroup.map((item, index) => {
                return (
                  <Button
                    key={index}
                    icon={item.icon}
                    variant={
                      location.pathname.includes(item.path.pathname) || (index === 0 && location.pathname === '/')
                        ? 'primary'
                        : undefined
                    }
                    onClick={() => {
                      navigate({
                        ...item.path,
                      });
                    }}
                  >
                    {item.content}
                  </Button>
                );
              })}
            </ButtonGroup>
          </div>
        ) : null}
        {props.fullWidth && (props.headers || null)}
        {!isCache ? (
          <div className="skeleton-page">
            <Page {...props}>
              <SkeletonPage />
            </Page>
          </div>
        ) : null}
        <Page fullWidth={props.fullWidth} {...props}>
          {(!props.fullWidth && props.headers) || null}
          <div>
            <div>{props.children}</div>
            <RegularText>
              <div className="layout-footer">
                <span>Need help? Please view&nbsp;</span>
                <a
                  href={USER_GUIDE_LINK.GUIDE_LINK}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'flex',
                    textDecoration: 'none',
                    color: 'var(--p-color-text-link)',
                  }}
                >
                  our document guideline
                  <Icon source={ExternalIcon} />
                </a>
              </div>
            </RegularText>
          </div>
        </Page>
      </Frame>
    </LayoutStyled>
  );
};
export default memo(Layout);
